import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { LoginPage } from "@/features/auth/login-page";
import { DashboardPage } from "@/features/dashboard/dashboard-page";
import { ClientsPage } from "@/features/clients/clients-page";
import { ClientsAnalyticsPage } from "@/features/clients/clients-analytics-page";
import { ClientEditPage, ClientNewPage } from "@/features/clients/client-form-page";
import { ClientDetailLayout } from "@/features/clients/detail/client-detail-layout";
import { ClientProfilePage } from "@/features/clients/detail/profile-page";
import { ClientContactsPage } from "@/features/clients/detail/contacts-page";
import { ClientAddressesPage } from "@/features/clients/detail/addresses-page";
import { ClientNotesPage } from "@/features/clients/detail/notes-page";
import { ClientRemindersPage } from "@/features/clients/detail/reminders-page";
import { ClientFilesPage } from "@/features/clients/detail/files-page";
import { ClientLedgerPage } from "@/features/clients/detail/ledger-page";
import { MaterialsPage } from "@/features/materials/materials-page";
import { MaterialEditPage, MaterialNewPage } from "@/features/materials/material-form-page";
import { LocationsPage } from "@/features/locations/locations-page";
import { MachinesPage } from "@/features/machines/machines-page";
import { FleetPage } from "@/features/fleet/fleet-page";
import { UsersPage } from "@/features/users/users-page";
import { PlanningPage } from "@/features/planning/planning-page";
import {
  PlanningEditPage,
  PlanningNewPage,
} from "@/features/planning/planning-form-page";
import { PlanningDetailPage } from "@/features/planning/planning-detail-page";
import { ProductionPage } from "@/features/production/production-page";
import { QualityPage } from "@/features/quality/quality-page";
import { DispatchPage } from "@/features/dispatch/dispatch-page";
import { DispatchEditPage, DispatchNewPage } from "@/features/dispatch/dispatch-form-page";
import { DispatchDetailPage } from "@/features/dispatch/dispatch-detail-page";
import { SettingsLayout } from "@/features/settings/settings-layout";
import { CompanySettingsPage } from "@/features/settings/company-page";
import { DefaultsSettingsPage } from "@/features/settings/defaults-page";
import { PreferencesSettingsPage } from "@/features/settings/preferences-page";
import { TaxesSettingsPage } from "@/features/settings/taxes-page";
import { VendorsPage } from "@/features/vendors/vendors-page";
import { VendorEditPage, VendorNewPage } from "@/features/vendors/vendor-form-page";
import { VendorDetailLayout } from "@/features/vendors/detail/vendor-detail-layout";
import { VendorProfilePage } from "@/features/vendors/detail/profile-page";
import { VendorContactsPage } from "@/features/vendors/detail/contacts-page";
import { VendorAddressesPage } from "@/features/vendors/detail/addresses-page";
import { VendorNotesPage } from "@/features/vendors/detail/notes-page";
import { VendorLedgerPage } from "@/features/vendors/detail/ledger-page";
import { VendorPaymentsTabPage } from "@/features/vendors/detail/vendor-payments-page";
import { MasterLedgerPage } from "@/features/books/master-ledger-page";
import { ExpensesPage } from "@/features/expenses/expenses-page";
import { PurchaseOrdersPage } from "@/features/purchase/purchase-orders-page";
import {
  PurchaseOrderEditPage,
  PurchaseOrderNewPage,
} from "@/features/purchase/purchase-order-form-page";
import { PurchaseOrderDetailPage } from "@/features/purchase/purchase-order-detail-page";
import { GoodsReceiptPage } from "@/features/purchase/goods-receipt-page";
import { InventoryPage } from "@/features/inventory/inventory-page";
import { InventoryBatchDetailPage } from "@/features/inventory/batch-detail-page";
import { InventoryLookupPage } from "@/features/inventory/lookup-page";
import { ScrapPage } from "@/features/scrap/scrap-page";
import { QuotationsPage } from "@/features/quotations/quotations-page";
import {
  QuotationEditPage,
  QuotationNewPage,
} from "@/features/quotations/quotation-form-page";
import { QuotationDetailPage } from "@/features/quotations/quotation-detail-page";
import { ClientQuotationsPage } from "@/features/clients/detail/client-quotations-page";
import { ClientOrdersPage } from "@/features/clients/detail/client-orders-page";
import { SalesOrdersPage } from "@/features/sales-orders/sales-orders-page";
import {
  SalesOrderEditPage,
  SalesOrderNewPage,
} from "@/features/sales-orders/sales-order-form-page";
import { SalesOrderDetailPage } from "@/features/sales-orders/sales-order-detail-page";
import { SalesOrderFulfillmentPage } from "@/features/sales-orders/fulfillment-page";
import { InvoicesPage } from "@/features/invoices/invoices-page";
import { InvoiceEditPage, InvoiceNewPage } from "@/features/invoices/invoice-form-page";
import { InvoiceDetailPage } from "@/features/invoices/invoice-detail-page";
import { ClientInvoicesPage } from "@/features/clients/detail/client-invoices-page";
import { ClientPaymentsPage } from "@/features/clients/detail/client-payments-page";
import { PaymentsPage } from "@/features/payments/payments-page";
import { CreditNotesPage } from "@/features/credit-notes/credit-notes-page";
import {
  CreditNoteEditPage,
  CreditNoteNewPage,
} from "@/features/credit-notes/credit-note-form-page";
import { CreditNoteDetailPage } from "@/features/credit-notes/credit-note-detail-page";
import { ClientCreditNotesPage } from "@/features/clients/detail/client-credit-notes-page";
import { VendorOrdersPage } from "@/features/vendors/detail/orders-page";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider } from "@/lib/theme";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";

function RootComponent() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    </ThemeProvider>
  );
}

const rootRoute = createRootRoute({
  component: RootComponent,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: async () => {
    if (!isSupabaseConfigured) return;
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: LoginPage,
});

function ProtectedLayout() {
  const { loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Loading session…
      </div>
    );
  }

  return <AppShell />;
}

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async () => {
    if (!isSupabaseConfigured) return;
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      throw redirect({ to: "/login" });
    }
  },
  component: ProtectedLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/dashboard" });
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: DashboardPage,
});

const booksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/books",
  component: MasterLedgerPage,
});

const expensesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/expenses",
  component: ExpensesPage,
});

const purchaseRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchase",
  validateSearch: (search: Record<string, unknown>): { vendor?: string } => ({
    vendor: typeof search.vendor === "string" ? search.vendor : undefined,
  }),
  component: PurchaseOrdersPage,
});

const purchaseOrderNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchase/orders/new",
  component: PurchaseOrderNewPage,
});

function PurchaseOrderDetailRouteComponent() {
  const { poId } = purchaseOrderDetailRoute.useParams();
  return <PurchaseOrderDetailPage poId={poId} />;
}

const purchaseOrderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchase/orders/$poId",
  component: PurchaseOrderDetailRouteComponent,
});

function PurchaseOrderEditRouteComponent() {
  const { poId } = purchaseOrderEditRoute.useParams();
  return <PurchaseOrderEditPage poId={poId} />;
}

const purchaseOrderEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchase/orders/$poId/edit",
  component: PurchaseOrderEditRouteComponent,
});

function PurchaseOrderReceiveRouteComponent() {
  const { poId } = purchaseOrderReceiveRoute.useParams();
  return <GoodsReceiptPage poId={poId} />;
}

const purchaseOrderReceiveRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchase/orders/$poId/receive",
  component: PurchaseOrderReceiveRouteComponent,
});

const inventoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory",
  component: InventoryPage,
});

function InventoryBatchDetailRouteComponent() {
  const { batchId } = inventoryBatchDetailRoute.useParams();
  return <InventoryBatchDetailPage batchId={batchId} />;
}

const inventoryBatchDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inventory/$batchId",
  component: InventoryBatchDetailRouteComponent,
});

const lookupRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/lookup",
  validateSearch: (search: Record<string, unknown>): { batch?: string; q?: string } => ({
    batch: typeof search.batch === "string" ? search.batch : undefined,
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  component: InventoryLookupPage,
});

const scrapRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/scrap",
  component: ScrapPage,
});

const quotationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotations",
  validateSearch: (search: Record<string, unknown>): { client?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: QuotationsPage,
});

const quotationNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotations/new",
  validateSearch: (search: Record<string, unknown>): { client?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: QuotationNewPage,
});

function QuotationDetailRouteComponent() {
  const { qtId } = quotationDetailRoute.useParams();
  return <QuotationDetailPage qtId={qtId} />;
}

const quotationDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotations/$qtId",
  component: QuotationDetailRouteComponent,
});

function QuotationEditRouteComponent() {
  const { qtId } = quotationEditRoute.useParams();
  return <QuotationEditPage qtId={qtId} />;
}

const quotationEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quotations/$qtId/edit",
  component: QuotationEditRouteComponent,
});

const salesOrdersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sales-orders",
  validateSearch: (search: Record<string, unknown>): { client?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: SalesOrdersPage,
});

const salesOrderNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sales-orders/new",
  validateSearch: (search: Record<string, unknown>): { client?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: SalesOrderNewPage,
});

function SalesOrderDetailRouteComponent() {
  const { soId } = salesOrderDetailRoute.useParams();
  return <SalesOrderDetailPage soId={soId} />;
}

const salesOrderDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sales-orders/$soId",
  component: SalesOrderDetailRouteComponent,
});

function SalesOrderEditRouteComponent() {
  const { soId } = salesOrderEditRoute.useParams();
  return <SalesOrderEditPage soId={soId} />;
}

const salesOrderEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sales-orders/$soId/edit",
  component: SalesOrderEditRouteComponent,
});

function SalesOrderFulfillRouteComponent() {
  const { soId } = salesOrderFulfillRoute.useParams();
  return <SalesOrderFulfillmentPage soId={soId} />;
}

const salesOrderFulfillRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/sales-orders/$soId/fulfill",
  component: SalesOrderFulfillRouteComponent,
});

const invoicesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/invoices",
  validateSearch: (search: Record<string, unknown>): { client?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: InvoicesPage,
});

const invoiceNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/invoices/new",
  validateSearch: (search: Record<string, unknown>): { client?: string; so?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
    so: typeof search.so === "string" ? search.so : undefined,
  }),
  component: InvoiceNewPage,
});

function InvoiceDetailRouteComponent() {
  const { invId } = invoiceDetailRoute.useParams();
  return <InvoiceDetailPage invId={invId} />;
}

const invoiceDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/invoices/$invId",
  component: InvoiceDetailRouteComponent,
});

function InvoiceEditRouteComponent() {
  const { invId } = invoiceEditRoute.useParams();
  return <InvoiceEditPage invId={invId} />;
}

const invoiceEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/invoices/$invId/edit",
  component: InvoiceEditRouteComponent,
});

const paymentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/payments",
  validateSearch: (search: Record<string, unknown>): { client?: string; invoice?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
    invoice: typeof search.invoice === "string" ? search.invoice : undefined,
  }),
  component: PaymentsPage,
});

const creditNotesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/credit-notes",
  validateSearch: (search: Record<string, unknown>): { client?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: CreditNotesPage,
});

const creditNoteNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/credit-notes/new",
  validateSearch: (search: Record<string, unknown>): { client?: string; invoice?: string } => ({
    client: typeof search.client === "string" ? search.client : undefined,
    invoice: typeof search.invoice === "string" ? search.invoice : undefined,
  }),
  component: CreditNoteNewPage,
});

function CreditNoteDetailRouteComponent() {
  const { cnId } = creditNoteDetailRoute.useParams();
  return <CreditNoteDetailPage cnId={cnId} />;
}

const creditNoteDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/credit-notes/$cnId",
  component: CreditNoteDetailRouteComponent,
});

function CreditNoteEditRouteComponent() {
  const { cnId } = creditNoteEditRoute.useParams();
  return <CreditNoteEditPage cnId={cnId} />;
}

const creditNoteEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/credit-notes/$cnId/edit",
  component: CreditNoteEditRouteComponent,
});

const materialsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/materials",
  component: MaterialsPage,
});

const materialNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/materials/new",
  component: MaterialNewPage,
});

function MaterialEditRouteComponent() {
  const { materialId } = materialEditRoute.useParams();
  return <MaterialEditPage materialId={materialId} />;
}

const materialEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/materials/$materialId/edit",
  component: MaterialEditRouteComponent,
});

const locationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/locations",
  component: LocationsPage,
});

const machinesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/machines",
  component: MachinesPage,
});

const fleetRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fleet",
  component: FleetPage,
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: UsersPage,
});

const planningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planning",
  component: PlanningPage,
});

const planningNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planning/new",
  component: PlanningNewPage,
});

function PlanningDetailRouteComponent() {
  const { planId } = planningDetailRoute.useParams();
  return <PlanningDetailPage planId={planId} />;
}

const planningDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planning/$planId",
  component: PlanningDetailRouteComponent,
});

function PlanningEditRouteComponent() {
  const { planId } = planningEditRoute.useParams();
  return <PlanningEditPage planId={planId} />;
}

const planningEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/planning/$planId/edit",
  component: PlanningEditRouteComponent,
});

const productionRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/production",
  component: ProductionPage,
});

const qualityRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/quality",
  component: QualityPage,
});

const dispatchRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dispatch",
  validateSearch: (search: Record<string, unknown>): { so?: string; client?: string } => ({
    so: typeof search.so === "string" ? search.so : undefined,
    client: typeof search.client === "string" ? search.client : undefined,
  }),
  component: DispatchPage,
});

const dispatchNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dispatch/new",
  validateSearch: (search: Record<string, unknown>): { so?: string } => ({
    so: typeof search.so === "string" ? search.so : undefined,
  }),
  component: DispatchNewPage,
});

function DispatchDetailRouteComponent() {
  const { dnId } = dispatchDetailRoute.useParams();
  return <DispatchDetailPage dnId={dnId} />;
}

const dispatchDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dispatch/$dnId",
  component: DispatchDetailRouteComponent,
});

function DispatchEditRouteComponent() {
  const { dnId } = dispatchEditRoute.useParams();
  return <DispatchEditPage dnId={dnId} />;
}

const dispatchEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dispatch/$dnId/edit",
  component: DispatchEditRouteComponent,
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsLayout,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/settings/company" });
  },
});

const settingsCompanyRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/company",
  component: CompanySettingsPage,
});

const settingsDefaultsRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/defaults",
  component: DefaultsSettingsPage,
});

const settingsPreferencesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/preferences",
  component: PreferencesSettingsPage,
});

const settingsTaxesRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: "/taxes",
  component: TaxesSettingsPage,
});

const vendorsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/vendors",
  component: VendorsPage,
});

const vendorNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/vendors/new",
  component: VendorNewPage,
});

function VendorEditRouteComponent() {
  const { vendorId } = vendorEditRoute.useParams();
  return <VendorEditPage vendorId={vendorId} />;
}

const vendorEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/vendors/$vendorId/edit",
  component: VendorEditRouteComponent,
});

const vendorDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/vendors/$vendorId",
  component: VendorDetailLayout,
});

const vendorDetailIndexRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/vendors/$vendorId/profile",
      params: { vendorId: params.vendorId },
    });
  },
});

const vendorProfileRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/profile",
  component: VendorProfilePage,
});

const vendorContactsRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/contacts",
  component: VendorContactsPage,
});

const vendorAddressesRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/addresses",
  component: VendorAddressesPage,
});

const vendorNotesRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/notes",
  component: VendorNotesPage,
});

const vendorLedgerRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/ledger",
  component: VendorLedgerPage,
});

const vendorOrdersRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/orders",
  component: VendorOrdersPage,
});

const vendorPaymentsRoute = createRoute({
  getParentRoute: () => vendorDetailRoute,
  path: "/payments",
  component: VendorPaymentsTabPage,
});

const clientsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clients",
  component: ClientsPage,
});

const clientNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clients/new",
  component: ClientNewPage,
});

const clientsAnalyticsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clients/analytics",
  component: ClientsAnalyticsPage,
});

const clientDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clients/$clientId",
  component: ClientDetailLayout,
});

function ClientEditRouteComponent() {
  const { clientId } = clientEditRoute.useParams();
  return <ClientEditPage clientId={clientId} />;
}

const clientEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/clients/$clientId/edit",
  component: ClientEditRouteComponent,
});

const clientDetailIndexRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/clients/$clientId/profile",
      params: { clientId: params.clientId },
    });
  },
});

const clientProfileRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/profile",
  component: ClientProfilePage,
});

const clientContactsRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/contacts",
  component: ClientContactsPage,
});

const clientAddressesRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/addresses",
  component: ClientAddressesPage,
});

const clientNotesRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/notes",
  component: ClientNotesPage,
});

const clientRemindersRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/reminders",
  component: ClientRemindersPage,
});

const clientFilesRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/files",
  component: ClientFilesPage,
});

const clientQuotationsRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/quotations",
  component: ClientQuotationsPage,
});
const clientOrdersRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/orders",
  component: ClientOrdersPage,
});
const clientInvoicesRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/invoices",
  component: ClientInvoicesPage,
});
const clientPaymentsRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/payments",
  component: ClientPaymentsPage,
});
const clientCreditNotesRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/credit-notes",
  component: ClientCreditNotesPage,
});
const clientStatementRoute = createRoute({
  getParentRoute: () => clientDetailRoute,
  path: "/statement",
  component: ClientLedgerPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  appRoute.addChildren([
    dashboardRoute,
    booksRoute,
    expensesRoute,
    purchaseRoute,
    purchaseOrderNewRoute,
    purchaseOrderDetailRoute,
    purchaseOrderEditRoute,
    purchaseOrderReceiveRoute,
    inventoryRoute,
    inventoryBatchDetailRoute,
    lookupRoute,
    scrapRoute,
    quotationsRoute,
    quotationNewRoute,
    quotationDetailRoute,
    quotationEditRoute,
    salesOrdersRoute,
    salesOrderNewRoute,
    salesOrderDetailRoute,
    salesOrderEditRoute,
    salesOrderFulfillRoute,
    invoicesRoute,
    invoiceNewRoute,
    invoiceDetailRoute,
    invoiceEditRoute,
    paymentsRoute,
    creditNotesRoute,
    creditNoteNewRoute,
    creditNoteDetailRoute,
    creditNoteEditRoute,
    materialsRoute,
    materialNewRoute,
    materialEditRoute,
    locationsRoute,
    machinesRoute,
    fleetRoute,
    usersRoute,
    planningRoute,
    planningNewRoute,
    planningDetailRoute,
    planningEditRoute,
    productionRoute,
    qualityRoute,
    dispatchRoute,
    dispatchNewRoute,
    dispatchDetailRoute,
    dispatchEditRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsCompanyRoute,
      settingsDefaultsRoute,
      settingsPreferencesRoute,
      settingsTaxesRoute,
    ]),
    vendorsRoute,
    vendorNewRoute,
    vendorEditRoute,
    vendorDetailRoute.addChildren([
      vendorDetailIndexRoute,
      vendorProfileRoute,
      vendorContactsRoute,
      vendorAddressesRoute,
      vendorNotesRoute,
      vendorLedgerRoute,
      vendorOrdersRoute,
      vendorPaymentsRoute,
    ]),
    clientsRoute,
    clientNewRoute,
    clientsAnalyticsRoute,
    clientEditRoute,
    clientDetailRoute.addChildren([
      clientDetailIndexRoute,
      clientProfileRoute,
      clientContactsRoute,
      clientAddressesRoute,
      clientNotesRoute,
      clientRemindersRoute,
      clientFilesRoute,
      clientQuotationsRoute,
      clientOrdersRoute,
      clientInvoicesRoute,
      clientPaymentsRoute,
      clientCreditNotesRoute,
      clientStatementRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
